import { withStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import { CircularProgress } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { FaCheckCircle, FaExclamationCircle, FaRegCircle, FaTrashAlt } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { saveAttachments, submitAddProjectRequest, submitEditProjectRequest } from "../../../redux/actions/lenderRequestActions";
import { DocumentUpload } from "../../LenderRequests/Shared/DocumentUpload";
import { uploadFile } from "../../shared/services/FileUploadService";
import { isOverflowingX } from "../../shared/utilities/isOverflowing";
import "./AttachmentsPage.css";

// TODO: Confirm this is correct
const techSupportUrl = "https://singlefamily.fanniemae.com/applications-technology/technology-support-center";

const sx = {
  tooltip: {
    color: "black",
    backgroundColor: "white",
    border: "1px solid #E6E8ED",
    fontSize: 14,
  },
  arrow: {
    color: "white",
    "&:before": {
      border: "1px solid #E6E8ED",
    },
  }
};

const StyledTooltip = withStyles(sx)(Tooltip);

// Show tooltip only if text is truncated
const CustomTooltip = (props) => {
  return <StyledTooltip
    {...props}
    disableFocusListener
    disableTouchListener
    disableHoverListener={props.target && !isOverflowingX(props.target)}
  />;
};

const AttachmentsPage = ({ incrementCounter, decrementCounter }) => {  
  const [showValidation, setShowValidation] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  
  const dispatch = useDispatch(); 
  const requirements = useSelector(store => store.lenderRequestReducer.requiredDocTypes);
  const optional = useSelector(store => store.lenderRequestReducer.optionalDocTypes);
  const projectInfo = useSelector(store => store.lenderRequestReducer.projectInfo);
  const requestTypeName = useSelector(store => store.lenderRequestReducer.requestTypeName);
  const requestId = useSelector(store => store.lenderRequestReducer.requestId);

  const [requiredDropdown, setRequiredDropdown] = useState([]);
  const [optionalDropdown, setOptionalDropdown] = useState([]);
  const [unfilledRequirements, setUnfilledRequirements] = useState();
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (optional && requirements && uploads) {
      if (!requirements.flat().length) {
        // should be impossible
        console.log('No required documents, skipping to next step');

        getOrCreateRequestId().then(() => 
          incrementCounter()
        );

        return;
      }

      const addedDocTypes = new Set(uploads.map(fileObj => fileObj?.docType?.documentRankingId));
      const remainingNested = requirements.map(reqSet => 
        reqSet.some(req => addedDocTypes.has(req.documentRankingId))
          ? [] 
          : reqSet
      );
      const fulfilledNested = requirements.map(reqSet => 
        reqSet.some(req => addedDocTypes.has(req.documentRankingId))
          ? reqSet
          : []
      );

      const remainingFlat = new Set(remainingNested.flat());
      const fulfilled = new Set(fulfilledNested.flat());
      const fulfilledOrOptional = new Set([ ...fulfilled, ...optional.flat() ]);

      // const subTypeLookup = [...requirements.flat(), ...optional.flat()].reduce((lookup, docTypeObj) => ({
      //   ...lookup,
      //   [docTypeObj.name]: docTypeObj,
      //   [docTypeObj.documentRankingId]: docTypeObj,
      // }), {});

      console.log('requirements updated', {requirements, optional, uploads, addedDocTypes, remainingNested, fulfilledNested, remainingFlat, fulfilledOrOptional});

      // setDocSubTypeLookup(subTypeLookup);      
      setRequiredDropdown([ ...remainingFlat ]);
      setOptionalDropdown([ ...fulfilledOrOptional ]);
      setUnfilledRequirements(remainingNested);
      setUploadFailed(false);
    }    
  }, [optional, requirements, uploads]);

  useEffect(() => {
    if (unfilledRequirements && requiredDropdown) {
      setIsValid(!requiredDropdown.length);
    }
  }, [unfilledRequirements, requiredDropdown])

  function handleAddFile(fileObj) {
    const files = [ ...uploads ];    
    files.push(fileObj);
    setUploads(files);
    // console.log('handleAddFile', {fileObj, files, uploads})
  }

  function handleRemoveFile(index) {
    const files = [ ...uploads ];    
    files.splice(index, 1);
    setUploads(files);
    setShowValidation(true);
    console.log('handleRemoveFile', {index, files, uploads})
  }

  function handleBackButton(e) {
    e.preventDefault();    
    decrementCounter();
  }

  async function getOrCreateRequestId() {    
    const userRole = sessionStorage.getItem('role');

    console.groupCollapsed('getOrCreateRequestId');
    console.log('userRole', userRole);
    console.log('requestTypeName', requestTypeName);
    console.log('requestId', requestId);
    console.log('projectInfo', projectInfo);
    console.groupEnd();

    if (!requestId) {
      try {
        // todo: convert to callback pattern
        const promise = requestTypeName === "Lender Add Project"
            ? dispatch(submitAddProjectRequest(projectInfo))
            : dispatch(submitEditProjectRequest(projectInfo.projectId));

        console.groupCollapsed('getOrCreateRequestId result');
        console.log('requestTypeName', requestTypeName);
        console.log('promise', promise);
        
        const {requestId, projectId, phaseId} = await promise;

        console.log('results', {requestId, projectId, phaseId});
        console.groupEnd();

        return {requestId, projectId, phaseId};
      } catch (error) {
        setUploadFailed(true); 
        setUploading(false);
        console.error("Error during getOrCreateRequestId", error);
        throw error;
      }
    }

    console.log('Using existing requestId', requestId);
    return requestId;
  }

  async function handleSubmit(e) {
    e.preventDefault();    

    setUploadFailed(false);
    setShowValidation(true);
    
    if (isValid) {
      setUploading(true);

      // phaseId is currently unused here, but this component will be generalized in the future
      const {requestId, projectId, phaseId} = await getOrCreateRequestId();      
      const uploadResults = [];

      if (!requestId) {
        console.error('No requestId found in handleSubmit', requestId);
        setUploadFailed(true); 
        setUploading(false);
        return;
      }

      const uploadTasks = uploads.map((fileObj, i) => {
        let resolve, reject;

        const promise = new Promise((_resolve, _reject) => {
          resolve = _resolve;
          reject = _reject;
        });

        const onSuccess = (data) => {
          console.log('upload success callback ' + i, {i, data, fileObj})
          const result = {
            documentRankingId: fileObj.docType.documentRankingId,
            docMapId: data?.docMapId,
            note: fileObj.note
          };
          uploadResults.push(result);
          resolve(result);
        };   

        const onFail = (error) => {
          console.error('upload error callback ' + i, {i, error, fileObj});
          reject(error);
        };

        uploadFile(
          fileObj.file, 
          fileObj.note, 
          null, 
          phaseId,
          projectId,
          fileObj.file.name,
          onSuccess, 
          onFail
        )
        .catch(onFail);      
        
        return promise;
      });

      const results = await Promise.allSettled(uploadTasks);
      const succeeded = results.filter(result => result.status === "fulfilled");
      const failed = results.filter(result => result.status === "rejected");

      console.log('all uploads complete', {uploadResults, results, succeeded, failed})

      if (succeeded.length === results.length) {
        const result = await dispatch(saveAttachments(requestId, uploadResults, incrementCounter));
        setUploadFailed(!result);
      } else {
        console.error('Some uploads failed', {results, succeeded, failed})
        setUploadFailed(true);
      }
      
      setUploading(false);
    }
  }

  function describeRequirement(reqSet) {
    // console.log('describeRequirement', reqSet)
    const labels = reqSet.map(req => req.name);

    return reqSet.length === 1
      ? labels[0]
      : reqSet.length === 2
      ? `${labels[0]} and/or ${labels[1]}`
      : `At least one of ${labels.slice(0, -1).join(", ")}, and/or ${labels[labels.length - 1]}`;
  }

  function getBullet(index) {
    // all missing required docTypes in this subset, if any
    const missingDocs = unfilledRequirements[index];

    return missingDocs && !missingDocs.length // requirement fulfilled
      ? <FaCheckCircle className="req-bullet completed" />
      : !showValidation || !missingDocs  // validation not triggered or list not initialized
      ? <FaRegCircle className="req-bullet default" />
      : <FaExclamationCircle className="req-bullet error" />
  }

  const fileNameRefs = useRef([]);
  const fileNoteRefs = useRef([]);
  const fileDocTypeRefs = useRef([]);

  return (
    <form className={"upload-docs-form " + (showValidation ? "show-validation" : "")}>
      <section className="title-section">
        <h1>{requestTypeName || "Lender"} Request</h1>
        <h6>Section 2 of 3 - Upload Documents</h6>
      </section>
    
      {unfilledRequirements ? (
        <section className="main-section">
          <p>Please upload the following documents:</p>
          <ul className="req-list">
            {requirements.map((reqSet, index) => 
              <li key={index}>
                {getBullet(index)}
                <div className="req-desc">
                  {describeRequirement(reqSet)}
                </div>
              </li>
            )}
          </ul>
          {uploads.length ? ( 
            <div className="uploaded-files-list">
              <div className="uploaded-files-row header-row">
                <div className="file-doctype">Document Type</div>
                <div className="file-status-bar">File</div>             
                <div className="file-note">Note</div>
              </div>
              {uploads.map((fileObj, index) => (
                <div key={index} className="uploaded-files-row">
                  <CustomTooltip 
                    target={fileDocTypeRefs.current[index]} 
                    title={fileObj.docType.name} 
                    placement="top"
                  >
                    <div ref={ref => fileDocTypeRefs.current[index] = ref} className="file-doctype">
                      {fileObj.docType.name}
                    </div>
                  </CustomTooltip>

                  <div className="file-status-bar">
                    <CustomTooltip target={fileNameRefs.current[index]} title={fileObj.file.name} placement="top">
                      <div ref={ref => fileNameRefs.current[index] = ref} className="file-name">
                        {fileObj.file.name}
                      </div>
                    </CustomTooltip>
                    <div className="file-remove" onClick={() => handleRemoveFile(index)}>
                      <FaTrashAlt className="delete-button" />
                    </div>
                  </div>        

                  <CustomTooltip 
                    target={fileNoteRefs.current[index]} 
                    title={fileObj.note} 
                    placement="top"
                  >
                    <div ref={ref => fileNoteRefs.current[index] = ref} className="file-note">
                      {fileObj.note ? fileObj.note : (
                        <span className="empty-field">--</span>
                      )}
                    </div>
                  </CustomTooltip>
                </div>
              ))}
            </div>
          ) : (<></>)}
          <DocumentUpload
            requiredList={requiredDropdown} 
            optionalList={optionalDropdown} 
            onAddFile={fileObj => handleAddFile(fileObj)}
            />
        </section>
      ) : (<></>)}

      <div className="stepper-controls" style={{marginTop: "40px"}}>
        <div className="control-cell">
          <Button
            type="submit"
            className="back-button"
            onClick={handleBackButton}
            form="addProjectForm"
          >
            Back
          </Button>
        </div>
        <div className="control-cell">
          <Button
            type="submit"
            className={"btn-submit" + 
              (!isValid ? " btn-invalid" : "") +
              (uploading ? " btn-loading" : "")
            }
            disabled={uploading}
            onSubmit={handleSubmit}
            onClick={handleSubmit}
            form="addProjectForm"
          >
            {uploading ? (
              <CircularProgress
                style={{
                  display: "inline-flex",
                  padding: "10px",
                  color: "#fff",
                  justifySelf: "center",
                  alignSelf: "center"
                }}
              />
            ) : ("Upload Files")}              
          </Button>
        </div>
      </div>

      {uploadFailed ? (
        <div className="upload-error-message">
          <div>
            Failed to upload documents. Please try again. If the problem persists, 
            please <a href={techSupportUrl}>contact Fannie Mae technology support.</a>
          </div>
        </div>
      ) : (<></>)}
    </form>
  );
};

export default AttachmentsPage;
